/**
 * Inline SVG sparkline — no chart-lib deps. Renders a smooth path over
 * normalized [0,1] in a fixed viewBox so size is driven purely by CSS.
 *
 * Props:
 *   - values: number[]            REQUIRED, ordered chronologically
 *   - width, height: number       SVG pixel sizes (default 80×24)
 *   - showDots: boolean           draw small dots at each sample
 *   - ariaLabel: string           accessibility label
 */
export default function Sparkline({
  values,
  width = 80,
  height = 24,
  showDots = false,
  ariaLabel = 'trend',
}) {
  if (!Array.isArray(values) || values.length === 0) {
    return <span className="sparkline-empty" aria-label={ariaLabel}>—</span>;
  }
  if (values.length === 1) {
    // A single point isn't a trend; render a flat line so layout doesn't shift.
    const cy = height / 2;
    return (
      <svg className="sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
        <line x1={0} y1={cy} x2={width} y2={cy} className="sparkline-flat" />
        {showDots && <circle cx={width / 2} cy={cy} r={1.6} className="sparkline-dot" />}
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);
  // Pad vertically by 2px on each side so dots/strokes don't clip.
  const padY = 2;
  const innerH = height - padY * 2;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = padY + innerH - ((v - min) / range) * innerH;
    return [x, y];
  });
  const d = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ');

  const trendUp = values[values.length - 1] >= values[0];

  return (
    <svg
      className={`sparkline sparkline-${trendUp ? 'up' : 'down'}`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
    >
      <path d={d} className="sparkline-line" fill="none" />
      {showDots && points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={1.6} className="sparkline-dot" />
      ))}
    </svg>
  );
}
